import { useTranslation } from "react-i18next";

import type {
  CentinelaAlertScope,
  CentinelaAlertType,
} from "../../../features/centinela/types";

export const TRIP_ALERT_SCOPES: CentinelaAlertScope[] = ["viaje", "ambos"];

export const UNIT_ALERT_SCOPES: CentinelaAlertScope[] = ["global", "ambos"];

const CHIP_STYLES = {
  covered: "border-primary-500/40 bg-primary-500/10 text-content",
  uncovered: "border-line-soft bg-subtle text-content-faint",
  blocked: "border-dashed border-line bg-transparent text-content-faint",
} as const;

const DOT_STYLES = {
  covered: "bg-primary-500",
  uncovered: "bg-line",
  blocked: "bg-transparent ring-1 ring-inset ring-line",
} as const;

type CoverageState = keyof typeof CHIP_STYLES;

export interface CustodyAlertsCoverageProps {
  alerts: CentinelaAlertType[];
  covered: string[] | null;
  blockedIds?: string[];
}

export default function CustodyAlertsCoverage({
  alerts,
  covered,
  blockedIds = [],
}: CustodyAlertsCoverageProps) {
  const { t } = useTranslation("common");

  if (alerts.length === 0) return null;

  const stateOf = (alertId: string): CoverageState => {
    if (blockedIds.includes(alertId)) return "blocked";
    return covered?.includes(alertId) ? "covered" : "uncovered";
  };

  const coveredCount = alerts.filter((alert) => stateOf(alert.id) === "covered").length;

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <p className="text-[11px] font-medium text-content-secondary max-sm:text-xs">
          {t("monitoreoIa.custodyAlerts.label")}
        </p>
        {covered ? (
          <p className="shrink-0 text-[10.5px] tabular-nums text-content-faint">
            {t("monitoreoIa.custodyAlerts.count", {
              covered: coveredCount,
              total: alerts.length,
            })}
          </p>
        ) : null}
      </div>

      {covered ? (
        <div className="flex flex-wrap gap-1.5 max-sm:gap-2">
          {alerts.map((alert) => {
            const state = stateOf(alert.id);

            return (
              <span
                key={alert.id}
                title={`${alert.label}${alert.description ? ` — ${alert.description}` : ""}\n${t(
                  `monitoreoIa.custodyAlerts.state.${state}`,
                )}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[10.5px] font-medium max-sm:px-2.5 max-sm:py-1.5 max-sm:text-xs ${CHIP_STYLES[state]}`}
              >
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT_STYLES[state]}`} />
                {alert.label}
              </span>
            );
          })}
        </div>
      ) : (
        <p className="rounded-lg border border-line-soft bg-subtle px-2.5 py-2 text-[10.5px] leading-relaxed text-content-secondary max-sm:text-xs">
          {t("monitoreoIa.custodyAlerts.customProfile")}
        </p>
      )}

      <p className="mt-2 text-[10px] leading-relaxed text-content-faint max-sm:text-xs">
        {t("monitoreoIa.custodyAlerts.decidedByProfile")}
      </p>
    </div>
  );
}
